import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import studentPortalService from '../../services/studentPortalService';
import StudentAssignmentSolver from './StudentAssignmentSolver';
import { FileText, Upload, Clock, CheckCircle, Warning } from '@phosphor-icons/react';

const StudentAssignmentsSection = ({ academicTerms = [], selectedAcademicTerm: propSelectedTerm = null, selectedAssignmentId = null, onAssignmentSelected = null }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [submittingAssignmentId, setSubmittingAssignmentId] = useState(null);
  const [submissionFile, setSubmissionFile] = useState(null);
  const [showSolver, setShowSolver] = useState(false);
  const [selectedAssignmentIdLocal, setSelectedAssignmentIdLocal] = useState(null);
  const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(propSelectedTerm);

  const loadAssignments = React.useCallback(async (termId = selectedAcademicTerm, assignId = null) => {
    setLoading(true);
    try {
      const data = await studentPortalService.listMyAssignments(termId, assignId);
      setAssignments(data.assignments || []);
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [selectedAcademicTerm]);

  // Load assignments on mount and when term changes
  useEffect(() => {
    loadAssignments(selectedAcademicTerm, null);
  }, [selectedAcademicTerm, loadAssignments]);

  // Also load assignments if selectedAssignmentId is provided (from module quiz click)
  useEffect(() => {
    if (selectedAssignmentId != null) {
      setAssignments([]); // Reset to show loading state
      loadAssignments(selectedAcademicTerm, selectedAssignmentId);
    }
  }, [selectedAssignmentId, selectedAcademicTerm, loadAssignments]);

  // Auto-expand assignment when selectedAssignmentId prop changes (from module quiz click)
  useEffect(() => {
    if (selectedAssignmentId != null) {
      setExpandedAssignment(selectedAssignmentId);
      // Auto-open quiz solver if this is a quiz assignment with questions
      const assignment = assignments.find(a => a.id === selectedAssignmentId);
      if (assignment && assignment.questions && assignment.questions.length > 0) {
        setSelectedAssignmentIdLocal(selectedAssignmentId);
        setShowSolver(true);
      }
    }
  }, [selectedAssignmentId, assignments]);

  const handleSubmitAssignment = async (assignmentId) => {
    if (!submissionFile) {
      toast.error('Please select a file to submit');
      return;
    }

    setSubmittingAssignmentId(assignmentId);
    try {
      const formData = new FormData();
      formData.append('file', submissionFile);

      await studentPortalService.submitAssignmentFile(assignmentId, formData);

      toast.success('Assignment submitted successfully');
      setSubmissionFile(null);
      setExpandedAssignment(null);
      loadAssignments();
    } catch (error) {
      toast.error(error.message || 'Failed to submit assignment');
    } finally {
      setSubmittingAssignmentId(null);
    }
  };

  const getAssignmentStatus = (assignment) => {
    if (assignment.submitted_at) {
      return assignment.graded ? 'graded' : 'submitted';
    }
    return new Date() > new Date(assignment.due_date) ? 'overdue' : 'pending';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F59E0B',
      submitted: '#3B82F6',
      graded: '#10B981',
      overdue: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={18} />;
      case 'submitted':
      case 'graded':
        return <CheckCircle size={18} />;
      case 'overdue':
        return <Warning size={18} />;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Loading assignments...</div>;
  }

  return (
    <div className="assignments-section">
      <div className="assignments-header">
        <h3><FileText size={24} /> My Assignments</h3>
        {selectedAssignmentId ? (
          <p>Viewing: {assignments.length > 0 ? assignments[0].title : 'Assignment'}</p>
        ) : (
          <p>{assignments.length} assignment{assignments.length !== 1 ? 's' : ''}</p>
        )}
      </div>

      {/* Academic Term Filter - Hide when viewing specific assignment */}
      {!selectedAssignmentId && academicTerms && academicTerms.length > 0 && (
        <div style={{ marginBottom: '20px', marginTop: '16px' }}>
          <select 
            value={selectedAcademicTerm || ''} 
            onChange={(e) => setSelectedAcademicTerm(e.target.value || null)}
            style={{ 
              padding: '8px', 
              borderRadius: '4px', 
              border: '1px solid #0d0c0c', 
              width: '100%', 
              maxWidth: '300px', 
              color: '#111827', 
              backgroundColor: '#ffffff' 
            }}
            data-testid="assignment-term-filter"
          >
            <option value="">All Terms</option>
            {academicTerms.map(term => (
              <option key={term.id} value={term.id}>
                {term.term} - {term.academic_year}
              </option>
            ))}
          </select>
        </div>
      )}

      {assignments.length === 0 ? (
        <div className="empty-state-card">
          <FileText size={48} weight="duotone" />
          <h3>No Assignments Yet</h3>
          <p>Your assignments will appear here when teachers assign them</p>
        </div>
      ) : (
        <div className="assignments-list">
          {(selectedAssignmentId ? assignments.filter(a => a.id === selectedAssignmentId) : assignments).map(assignment => {
            const status = getAssignmentStatus(assignment);
            const isExpanded = expandedAssignment === assignment.id;

            return (
              <div 
                key={assignment.id} 
                className={`assignment-card ${status}`}
                style={{
                  borderLeft: `4px solid ${getStatusColor(status)}`,
                  marginBottom: '16px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setExpandedAssignment(isExpanded ? null : assignment.id)}
              >
                {/* Assignment Header */}
                <div className="assignment-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ marginBottom: '4px', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {assignment.title}
                    </h4>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
                      {assignment.subject_name} • By {assignment.teacher_name}
                    </p>
                    {assignment.graded && (
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '4px 8px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#15803d',
                        marginTop: '4px'
                      }}>
                        <CheckCircle size={14} weight="fill" />
                        Score: {assignment.score}/{assignment.max_score} ({assignment.percentage}%)
                      </div>
                    )}
                  </div>
                  <span 
                    className={`status-badge ${status}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      backgroundColor: `${getStatusColor(status)}20`,
                      color: getStatusColor(status),
                      fontSize: '12px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getStatusIcon(status)}
                    {status === 'graded' && assignment.percentage ? 
                      `${assignment.percentage}%` : 
                      status.charAt(0).toUpperCase() + status.slice(1)
                    }
                  </span>
                </div>

                {/* Assignment Meta */}
                <div className="assignment-meta" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '12px', fontSize: '12px', color: '#6b7280' }}>
                  <div>
                    <span style={{ fontWeight: '500', color: '#374151' }}>Posted:</span> {formatDate(assignment.created_at)}
                  </div>
                  <div>
                    <span style={{ fontWeight: '500', color: '#374151' }}>Due:</span> {formatDate(assignment.due_date)}
                  </div>
                  {assignment.submitted_at && (
                    <div>
                      <span style={{ fontWeight: '500', color: '#374151' }}>Submitted:</span> {formatDate(assignment.submitted_at)}
                    </div>
                  )}
                  {assignment.graded && assignment.graded_at && (
                    <div>
                      <span style={{ fontWeight: '500', color: '#374151' }}>Graded:</span> {formatDate(assignment.graded_at)}
                    </div>
                  )}
                </div>

                {/* Expandable Details */}
                {isExpanded && (
                  <div className="assignment-details" style={{ 
                    borderTop: '1px solid #e5e7eb', 
                    paddingTop: '16px',
                    marginTop: '12px'
                  }}>
                    {/* Description */}
                    {assignment.description && (
                      <div style={{ marginBottom: '16px' }}>
                        <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                          Description
                        </h5>
                        <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6' }}>
                          {assignment.description}
                        </p>
                      </div>
                    )}

                    {/* Grade Info (if graded) */}
                    {assignment.graded && (
                      <div style={{ 
                        backgroundColor: '#f0fdf4', 
                        borderRadius: '6px', 
                        padding: '12px',
                        marginBottom: '16px',
                        borderLeft: '3px solid #10B981'
                      }}>
                        <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#15803d', marginBottom: '8px' }}>
                          Grade
                        </h5>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span>Score: <strong>{assignment.score}/{assignment.max_score}</strong></span>
                          <span>Percentage: <strong>{assignment.percentage}%</strong></span>
                        </div>
                        {assignment.feedback && (
                          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff', borderRadius: '4px', fontSize: '12px', color: '#4b5563' }}>
                            <strong>Feedback:</strong> {assignment.feedback}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Submission Options (if not submitted, not graded, and not overdue) */}
                    {status === 'pending' && !assignment.graded && (
                      <>
                        {/* File Upload Option */}
                        <div style={{ 
                          backgroundColor: '#fef3c7', 
                          borderRadius: '6px', 
                          padding: '12px',
                          borderLeft: '3px solid #F59E0B',
                          marginBottom: '12px'
                        }}>
                          <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                            <Upload size={16} style={{ display: 'inline-block', marginRight: '6px' }} />
                            Submit Your Work (File Upload)
                          </h5>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="file"
                              onChange={(e) => setSubmissionFile(e.target.files?.[0] || null)}
                              disabled={submittingAssignmentId === assignment.id}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #d97706',
                                fontSize: '12px',
                              }}
                            />
                            <button
                              onClick={() => handleSubmitAssignment(assignment.id)}
                              disabled={!submissionFile || submittingAssignmentId === assignment.id}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                backgroundColor: '#F59E0B',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '12px',
                                opacity: !submissionFile || submittingAssignmentId === assignment.id ? 0.5 : 1
                              }}
                            >
                              {submittingAssignmentId === assignment.id ? 'Submitting...' : 'Submit'}
                            </button>
                          </div>
                        </div>

                        {/* Quiz/Questions Option */}
                        {assignment.questions && assignment.questions.length > 0 && (
                          <div style={{ 
                            backgroundColor: '#dbeafe', 
                            borderRadius: '6px', 
                            padding: '12px',
                            borderLeft: '3px solid #3b82f6'
                          }}>
                            <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                              <CheckCircle size={16} style={{ display: 'inline-block', marginRight: '6px' }} />
                              Answer Questions ({assignment.questions.length} questions)
                            </h5>
                            <p style={{ fontSize: '12px', color: '#1e40af', margin: '8px 0' }}>
                              {assignment.points_possible} points • {assignment.questions.filter(q => q.type !== 'essay').length} auto-gradable questions
                            </p>
                            <button
                              onClick={() => {
                                setSelectedAssignmentIdLocal(assignment.id);
                                setShowSolver(true);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                backgroundColor: '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '13px'
                              }}
                            >
                              Start Assignment
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Graded Assignment Notice */}
                    {assignment.graded && status !== 'overdue' && (
                      <div style={{ 
                        backgroundColor: '#f0fdf4', 
                        borderRadius: '6px', 
                        padding: '12px',
                        borderLeft: '3px solid #10B981',
                        color: '#15803d'
                      }}>
                        <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                          <CheckCircle size={16} weight="fill" style={{ display: 'inline-block', marginRight: '6px' }} />
                          Assignment Completed and Graded
                        </h5>
                        <p style={{ fontSize: '12px', margin: '4px 0' }}>
                          Your assignment has been graded. You can no longer submit or modify your work.
                        </p>
                      </div>
                    )}

                    {/* Overdue Warning */}
                    {status === 'overdue' && (
                      <div style={{ 
                        backgroundColor: '#fef2f2', 
                        borderRadius: '6px', 
                        padding: '12px',
                        borderLeft: '3px solid #EF4444',
                        color: '#991b1b'
                      }}>
                        <h5 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                          <Warning size={16} style={{ display: 'inline-block', marginRight: '6px' }} />
                          This assignment is overdue
                        </h5>
                        <p style={{ fontSize: '12px', margin: 0 }}>
                          You can still submit for partial credit. Contact your teacher if you need an extension.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Assignment Solver Modal */}
      {showSolver && selectedAssignmentIdLocal && (
        <StudentAssignmentSolver
          assignmentId={selectedAssignmentIdLocal}
          onClose={() => {
            setShowSolver(false);
            setSelectedAssignmentIdLocal(null);
            loadAssignments();  // Reload to show updated status
          }}
          onSubmissionComplete={() => {
            setShowSolver(false);
            setSelectedAssignmentIdLocal(null);
            loadAssignments();
          }}
        />
      )}
    </div>
  );
};

export default StudentAssignmentsSection;
